import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form/immutable';
import { Grid, Segment, Divider, Message } from 'semantic-ui-react';
import BrowserDetection from 'react-browser-detection';
import _ from 'lodash';
import { compose } from 'recompose';

import { authService } from '../../utils/auth-service';
import TextInput from '../../components/form-input-text';
import LoginProblemNotification from '../../components/login-problem-notification';
import PoweredByDyzio from '../../components/powered-by-dyzio';
import withIntercom from '../../hocs/with-guest-intercom';
import { EMAIL_REGULAR_EXPRESSION } from '../../utils/form-input-validation';
import { reportError, reportWarning } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';
import BrowserHandler from '../../utils/browser-handler';


import ThemeSelector from '../../themes/theme-selector';

const theme = ThemeSelector.theme();
const IS_WHITE_LABEL = ThemeSelector.isWhiteLabel();

const DYZIO_ERROR_MESSAGE_STEM = 'Please contact the dyzio team using the intercom button at the bottom right';
const GENERAL_ERROR_MESSAGE_STEM = 'Please contact the support team';

const getBlockedErrorMessage = isWhiteLabel => {
  const stem = isWhiteLabel ? GENERAL_ERROR_MESSAGE_STEM : DYZIO_ERROR_MESSAGE_STEM;
  return isWhiteLabel ? '' : `${stem} to get unblocked.`; // just return empty string for now
};

const getGeneralErrorMessage = isWhiteLabel => {
  const stem = isWhiteLabel ? GENERAL_ERROR_MESSAGE_STEM : DYZIO_ERROR_MESSAGE_STEM;
  return isWhiteLabel ? '' : `${stem} if you are having problems logging in.`; // just return empty string for now
};

const ERROR_MESSAGES = {
  blocked: getBlockedErrorMessage(IS_WHITE_LABEL),
  general: getGeneralErrorMessage(IS_WHITE_LABEL),
};


const LoginErrorComponent = ({ message }) => {
  if (message.match(/blocked/i)) {
    return (
      <Message
        icon="ban"
        error
        header="Sorry... Your account is blocked temporarily"
        content={ERROR_MESSAGES.blocked}
      />
    );
  }

  return (
    <Message
      icon="ban"
      error
      header={message}
      content={ERROR_MESSAGES.general}
    />
  );
};


class Login extends React.Component {
  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      loginError: null,
      isLoggingIn: false,
    };
  }
  getAuthParams = data => ({
    email: data.get('email'),
    password: data.get('password'),
  })


  login = data => {
    this.setState({
      loginError: null,
      isLoggingIn: true,
    });
    const loginData = {
      email: data.get('email'),
      loginType: 'emailPassword',
    };
    trackEvent('Login', loginData);
    authService.login(this.getAuthParams(data), error => {
      if (error) {
        if (_.includes(error.message, 'Wrong email or password')) {
          reportWarning('LOGIN_WARNING', error.description);
        } else {
          reportError('LOGIN_ERROR', error.description);
        }
        this.setState({ loginError: error.description, isLoggingIn: false });
      } else {
        this.setState({ loginError: false });
      }
    });
  }

  googleLogin = () => {
    trackEvent('Login', { loginType: 'google' });
    authService.socialLogin({
      connection: 'google-oauth2',
      scope: 'openid app_metadata',
    });
  }

  render() {
    const { handleSubmit } = this.props;
    const { loginError, isLoggingIn } = this.state;
    const loginProblem = false;

    const isVoxogram = window.location.hostname.match(/voxogram/);

    const logoStyle = {
      marginBottom: 35,
    };

    const whiteLabelStyle = {
      height: '60px',
      marginBottom: 35,
    };

    const viewportStyles = {
      backgroundColor: '#f7fafa',
      height: 'auto',
      minHeight: '100vh',
      padding: '64px 24px',
    };

    const signinBoxStyle = {
      background: 'white',
      border: '1px solid #e8e8e8',
      borderRadius: '4px',
      padding: '24px',
      maxWidth: '960px',
      margin: '0 auto',
    };

    const noBorderOrShadow = {
      border: 'none',
      'box-shadow': 'none',
    };

    const shortlistsPreviewStyle = {
      maxWidth: '100%',
      marginBottom: '10px',
    };

    const WhatsNew = () => (
      <div>
        <h3>{'What\'s new?'}</h3>
        <div className="spacer small" />
        <a
          href="http://help.dyzio.co/my-influencers-database/instagram-business-accounts"
          target="_blank"
          rel="noreferrer noopener"
        >
          <img
            alt="Instagram Business Accounts"
            src="/images/instagram-business-accounts.png"
            style={shortlistsPreviewStyle}
          />
        </a>
        <h4>
          <a
            href="http://help.dyzio.co/my-influencers-database/instagram-business-accounts"
            target="_blank"
            rel="noreferrer noopener"
          >
          Instagram Business Accounts!
          </a>
        </h4>
        <Divider />
        <p>Dyzio can now show you which of your influencers have an Instagram Business Account, which is essential for anyone posting branded or paid for content.</p>
      </div>
    );

    const derivedLogoStyle = IS_WHITE_LABEL ? whiteLabelStyle : logoStyle;

    return (
      <div style={viewportStyles}>
        <div className="align-center">
          <img src={theme.darkLogo.src} alt={theme.darkLogo.alt} style={derivedLogoStyle} />
        </div>
        <div style={signinBoxStyle}>
          <Grid columns={2} divided>
            <Grid.Row stretched stackable>
              <Grid.Column>
                <Segment style={noBorderOrShadow}>
                  { loginProblem && <LoginProblemNotification /> }
                  <button className="large stretch" onClick={this.googleLogin}>
                    <p>Log in with&nbsp;
                      <img className="google" src="/logos/google-logo.svg" alt="Google" tabIndex="-1" />
                    </p>
                  </button>
                  <form onSubmit={handleSubmit(this.login)}>
                    <div className="option-separator">
                      <div className="left" />
                      <div className="center">
                        <p>or</p>
                      </div>
                      <div className="right" />
                    </div>
                    <Field
                      name="email"
                      type="text"
                      component={TextInput}
                      placeholder="Email address"
                      img="/icons/email.svg"
                    />
                    <Field
                      name="password"
                      type="password"
                      component={TextInput}
                      placeholder="Password"
                      img="/icons/password.svg"
                    />
                    { loginError && <LoginErrorComponent message={loginError} /> }
                    <button type="submit" className="large rose stretch" disabled={isLoggingIn}>
                      <p>Log In</p>
                    </button>

                    {
                      !isVoxogram &&
                      <p className="info">
                        Forgotten your <a onClick={() => this.context.router.push('/resetpassword')}>password</a>?
                      </p>
                    }

                  </form>
                </Segment>
              </Grid.Column>
              <Grid.Column>
                <Segment style={noBorderOrShadow} textAlign="center">
                  {
                    theme.loginMessage || <WhatsNew />
                  }
                </Segment>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
        <br />
        <div className="align-center">
          <div className="browser-detect">
            <PoweredByDyzio />
            <BrowserDetection>
              {BrowserHandler.handle}
            </BrowserDetection>
          </div>
        </div>
      </div>
    );
  }
}

function validate(values) {
  const errors = {};

  const email = values.get('email');
  const password = values.get('password');

  if (!email) errors.email = 'Enter an email address.';
  if (email && !EMAIL_REGULAR_EXPRESSION.test(email)) errors.email = 'Enter a valid email address.';

  if (!password) errors.password = 'Enter a password.';

  return errors;
}

Login.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  email: state.invite.get('email'),
  initialValues: {
    email: state.invite.get('email'),
  },
});

export default compose(
  withIntercom,
  connect(mapStateToProps, null),
  reduxForm({
    form: 'LoginForm',
    validate,
  }),
)(Login);



/** WEBPACK FOOTER **
 ** ./src/containers/login/index.js
 **/